import { request } from "$/lib/api"
import bikeQueries from "$/lib/queries/bike-queries"
import jobQueries from "$/lib/queries/job-queries"
import {
  CreateStoreBike,
  MergeStoreBikes,
  Route,
  StoreBikeDetail,
  StoreBikeListItem,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

const fetchList = (storeCustomerId: string): Promise<StoreBikeListItem[]> =>
  request<StoreBikeListItem[]>({
    url: populateRoute(
      Route.STORE_BIKE__FIND_MANY_BY_CUSTOMER,
      storeCustomerId,
    ),
    method: "GET",
  })

const fetchDetail = (
  storeCustomerId: string,
  storeBikeId: string,
): Promise<StoreBikeDetail> =>
  request<StoreBikeDetail>({
    url: populateRoute(
      Route.STORE_BIKE__FIND_ONE,
      storeCustomerId,
      storeBikeId,
    ),
    method: "GET",
  })

const createStoreBike = (
  storeCustomerId: string,
  data: CreateStoreBike,
): Promise<StoreBikeListItem> =>
  request<StoreBikeListItem>({
    url: populateRoute(Route.STORE_BIKE__CREATE, storeCustomerId),
    method: "POST",
    data,
  })

const mergeStoreBikes = (
  storeCustomerId: string,
  data: MergeStoreBikes,
): Promise<StoreBikeDetail> =>
  request<StoreBikeDetail>({
    url: populateRoute(Route.STORE_BIKE__MERGE, storeCustomerId),
    method: "POST",
    data,
  })

const storeBikeQueries = {
  all: () => ["store-bikes"],
  list: (storeCustomerId: string) =>
    queryOptions({
      queryKey: [...storeBikeQueries.all(), "list", storeCustomerId],
      queryFn: () => fetchList(storeCustomerId),
    }),
  detail: (storeCustomerId: string, storeBikeId: string) =>
    queryOptions({
      queryKey: [
        ...storeBikeQueries.all(),
        "detail",
        storeCustomerId,
        storeBikeId,
      ],
      queryFn: () => fetchDetail(storeCustomerId, storeBikeId),
    }),
}

export const useStoreBikeMutations = (storeCustomerId: string) => {
  const queryClient = useQueryClient()

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: storeBikeQueries.all() })

  const createMutation = useMutation({
    mutationFn: (data: CreateStoreBike) =>
      createStoreBike(storeCustomerId, data),
    onSettled: invalidate,
  })

  const mergeMutation = useMutation({
    mutationFn: (data: MergeStoreBikes) =>
      mergeStoreBikes(storeCustomerId, data),
    onSettled: () =>
      Promise.all([
        invalidate(),
        // The bike picker on the job lists each bike once
        queryClient.invalidateQueries({ queryKey: bikeQueries.all() }),
        // The bike's latest job takes on the merged description
        queryClient.invalidateQueries({ queryKey: jobQueries.all() }),
      ]),
  })

  return { createMutation, mergeMutation }
}

export default storeBikeQueries
